/**
 * Move delayed result swaps below the scored viewport before React commits.
 *
 * `useLayoutEffect` is too late for CLS: the browser can attribute the DOM
 * mutation before the effect scrolls. Call this synchronously immediately
 * before the state update that replaces a form with a result.
 *
 * Pass `swapTarget`, the element the result will replace, carrying the same
 * scroll-margin the result anchors with: the page then lands where the
 * committed result will anchor itself, so there is one scroll, not two. Until
 * 2026-10-07 the kundali swap always went to top:0 first and the result then
 * scrolled back down to its own top, an up-then-down jump on every result-first
 * submit (C12). Without a target the page is anchored to the top (numerology,
 * whose result is anchored that way).
 */
export function anchorResultSwapBeforeCommit(swapTarget?: Element | null) {
  if (typeof window === "undefined") return;
  const root = document.documentElement;
  const scrollingElement = document.scrollingElement ?? root;
  const previousOverflowAnchor = root.style.overflowAnchor;

  // Chrome's native scroll anchoring can preserve the focused submit region
  // when React removes it, undoing a plain scroll in the same commit. Disable
  // that adjustment through the next paint and scroll synchronously so the
  // layout mutation starts with an already-updated viewport.
  root.style.overflowAnchor = "none";
  if (swapTarget) {
    swapTarget.scrollIntoView({ block: "start", behavior: "auto" });
  } else {
    scrollingElement.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      root.style.overflowAnchor = previousOverflowAnchor;
    });
  });
}
